import axios from 'axios';

class PushNotificationService {
  constructor() {
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
    this.registration = null;
    this.subscription = null;
  }

  async initialize() {
    if (!this.isSupported) {
      console.warn('Push notifications are not supported in this browser');
      return false;
    }

    try {
      // Register service worker
      this.registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered successfully');

      // Check if we already have a subscription
      this.subscription = await this.registration.pushManager.getSubscription();
      
      return true;
    } catch (error) {
      console.error('Error initializing push notifications:', error);
      return false;
    }
  }

  async requestPermission() {
    if (!this.isSupported) {
      throw new Error('Push notifications are not supported');
    }

    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      throw new Error('Permission not granted for push notifications');
    }

    return permission;
  }

  async subscribe(userUuid) {
    if (!this.registration) {
      throw new Error('Service worker not registered');
    }

    try {
      // Create push subscription
      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(process.env.REACT_APP_VAPID_PUBLIC_KEY)
      });

      console.log('Created subscription:', this.subscription);
      console.log('Subscription endpoint:', this.subscription.endpoint);
      console.log('Subscription keys:', this.subscription.keys);

      // Send subscription to server
      await this.sendSubscriptionToServer(userUuid, this.subscription);

      // Store subscription in localStorage for persistence
      localStorage.setItem('pushSubscription', JSON.stringify(this.subscription));
      localStorage.setItem('pushSubscriptionUserUuid', userUuid);

      console.log('Push subscription created successfully');
      return this.subscription;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      throw error;
    }
  }

  async unsubscribe() {
    if (!this.subscription) {
      return;
    }

    try {
      const success = await this.subscription.unsubscribe();
      if (success) {
        this.subscription = null;
        // Clear stored subscription
        localStorage.removeItem('pushSubscription');
        localStorage.removeItem('pushSubscriptionUserUuid');
        console.log('Push subscription removed successfully');
      }
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
    }
  }

  async sendSubscriptionToServer(userUuid, subscription) {
    try {
      const apiUrl = process.env.REACT_APP_MS_API_URL || 'http://localhost:3000';
      const url = `${apiUrl}/user/${userUuid}/push-subscription`;
      
      // Add device information to subscription
      const subscriptionWithDeviceInfo = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')),
          auth: this.arrayBufferToBase64(subscription.getKey('auth'))
        },
        userAgent: navigator.userAgent,
        deviceType: this.getDeviceType(),
        timestamp: new Date().toISOString()
      };
      
      console.log('Sending subscription to:', url);
      console.log('Original subscription:', subscription);
      console.log('Subscription with device info:', subscriptionWithDeviceInfo);
      console.log('Endpoint exists:', !!subscription.endpoint);
      console.log('Keys exist:', !!subscription.keys);
      
      await axios.post(url, subscriptionWithDeviceInfo, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      console.log('Subscription sent to server successfully');
    } catch (error) {
      console.error('Error sending subscription to server:', error);
      console.error('Error details:', error.response?.data || error.message);
      throw error;
    }
  }

  getDeviceType() {
    const userAgent = navigator.userAgent;
    if (/Mobile|Android|iPhone|iPad/.test(userAgent)) {
      return 'mobile';
    } else if (/Tablet|iPad/.test(userAgent)) {
      return 'tablet';
    } else {
      return 'desktop';
    }
  }

  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  urlBase64ToUint8Array(base64String) {
    if (!base64String) {
      throw new Error('VAPID public key is not defined. Please check your environment variables.');
    }
    
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  async isSubscribed() {
    try {
      // Check if notifications are permitted first
      if (Notification.permission !== 'granted') {
        return false;
      }
      
      if (!this.registration) {
        return false;
      }

      // First check if we have a subscription in the service worker
      this.subscription = await this.registration.pushManager.getSubscription();
      
      if (this.subscription) {
        return true;
      }

      // If no subscription in service worker, check localStorage
      const storedSubscription = localStorage.getItem('pushSubscription');
      if (storedSubscription) {
        try {
          const parsedSubscription = JSON.parse(storedSubscription);
          // Verify the subscription is still valid by checking if it has the required properties
          if (parsedSubscription.endpoint && parsedSubscription.keys) {
            console.log('Found stored subscription in localStorage');
            return true;
          }
        } catch (error) {
          console.error('Error parsing stored subscription:', error);
          // Clear invalid stored subscription
          localStorage.removeItem('pushSubscription');
          localStorage.removeItem('pushSubscriptionUserUuid');
        }
      }

      return false;
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return false;
    }
  }

  async showNotification(title, options = {}) {
    if (!this.registration) {
      console.warn('Service worker not registered');
      return;
    }

    const defaultOptions = {
      body: '',
      icon: '/favicon.png',
      badge: '/favicon.png',
      vibrate: [200, 100, 200],
      requireInteraction: true,
      ...options
    };

    await this.registration.showNotification(title, defaultOptions);
  }
}

export default new PushNotificationService();
